import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import SEOMeta from '@/components/base/SEOMeta';
import { articles, categories, hotTopics } from '@/mocks/articles';

export default function Home() {
  const { t } = useTranslation();
  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.slice(0, 6);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleNewsletterSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qrmvfnahb7382ovea0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  }, []);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黑料吃瓜',
    url: 'https://heiliao-chigua.com',
    description: t('site.fullName') as string,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://heiliao-chigua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料吃瓜',
    url: 'https://heiliao-chigua.com',
    description: t('site.fullName') as string,
    email: 'contact@heiliao-chigua.com',
    telephone: '400-888-6666',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座1808室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <SEOMeta
        title="首页"
        description="黑料吃瓜专属阵地，汇聚娱乐圈最新黑料吃瓜内容，明星八卦、网红翻车、热门瓜情实时更新。想看最劲爆的黑料吃瓜资讯？这里一站搞定，每日新鲜出炉，吃瓜群众必备。"
        keywords="黑料吃瓜,黑料吃瓜最新消息,明星黑料爆料,娱乐圈吃瓜,网红黑料翻车,热门八卦瓜情,吃瓜群众必看"
        ogTitle="黑料吃瓜·最新娱乐爆料聚合平台"
        ogDescription="黑料吃瓜专属阵地，汇聚娱乐圈最新黑料吃瓜内容。明星八卦、网红翻车、热门瓜情实时更新，吃瓜群众必备。"
        ogImage="https://readdy.ai/api/search-image?query=A%20vibrant%20Chinese%20entertainment%20news%20website%20hero%20banner%20with%20dramatic%20red%20and%20gold%20accents%2C%20modern%20layout%2C%20breaking%20news%20theme%2C%20clean%20and%20bold%20typography&width=1200&height=630&seq=og-homepage&orientation=landscape"
        schema={[homeSchema, orgSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20dramatic%20cinematic%20backdrop%20with%20warm%20golden%20and%20crimson%20lighting%2C%20entertainment%20newsroom%20vibe%2C%20breaking%20news%20aesthetic%2C%20luxurious%20dark%20red%20velvet%20curtains%2C%20stage%20spotlights%2C%20theatrical%20atmosphere%2C%20bold%20and%20vibrant&width=1800&height=900&seq=hero-bg-home&orientation=landscape"
            alt="黑料吃瓜背景"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/90 text-white text-sm font-medium mb-6 backdrop-blur-sm">
              🔥 24小时滚动更新
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
              {t('home.hero.title')}
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex items-center justify-center gap-4 flex-col sm:flex-row">
              <Link
                to="/category/remen-guaqing"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('home.hero.cta')}
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-white/15 text-white font-medium text-sm hover:bg-white/25 transition-all cursor-pointer backdrop-blur-sm whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5">
              <div className="w-1.5 h-3 rounded-full bg-white/60 animate-bounce"></div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 border border-background-200/60 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {t('home.featured')}
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选的劲爆爆料，不容错过</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredArticles.slice(0, 2).map((article) => (
                <ArticleCard key={article.id} {...article} variant="featured" />
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {featuredArticles.slice(2).map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                      {t('home.latest')}
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">最新出炉的热乎瓜，趁热吃</p>
                  </div>
                  <Link
                    to="/category/remen-guaqing"
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap"
                  >
                    {t('home.viewAll')}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {latestArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              </div>

              <div className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5 flex items-center gap-2">
                      <i className="ri-fire-line text-accent-500"></i>
                      {t('home.hotTopics')}
                    </h3>
                    <div className="space-y-1">
                      {hotTopics.map((topic, index) => (
                        <Link
                          key={topic.id}
                          to={`/article/${topic.id}`}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-200/60 transition-colors cursor-pointer group"
                        >
                          <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                            index < 3
                              ? 'bg-accent-500 text-white'
                              : 'bg-background-300 text-foreground-500'
                          }`}>
                            {index + 1}
                          </span>
                          <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1 flex-1">
                            {topic.title}
                          </span>
                          <span className="text-xs text-foreground-400 flex-shrink-0">
                            {(topic.count / 10000).toFixed(1)}万
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl p-6 mt-6 text-white">
                    <h3 className="font-heading text-lg font-bold mb-2">
                      {t('home.newsletter.title')}
                    </h3>
                    <p className="text-sm text-white/80 mb-5 leading-relaxed">
                      {t('home.newsletter.subtitle')}
                    </p>
                    <form
                      ref={formRef}
                      onSubmit={handleNewsletterSubmit}
                      data-readdy-form
                      id="newsletter-form"
                    >
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form-honeypot"
                      />
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder={t('home.newsletter.placeholder') as string}
                        className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder:text-white/50 text-sm outline-none focus:border-white/60 transition-colors mb-3"
                      />
                      <button
                        type="submit"
                        className="w-full px-4 py-3 rounded-xl bg-white text-primary-600 font-semibold text-sm hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
                      >
                        {t('home.newsletter.button')}
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-xs text-white/90 mt-2 text-center">{t('home.newsletter.success')}</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-xs text-white/90 mt-2 text-center">{formError || t('home.newsletter.error')}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}